import { useEffect, useMemo, useState } from "react";
import { getOrderHistory } from "../../api/user";
import "../../styles/profile.css";
import OrderItemImageComponent from "../imgs/OrderItemImage";
import { OrderItem } from "../../classes/Order";
import OrderBigItemImageComponent from "../imgs/OrderBigItemImageComponent";
import { getDisplayPrice } from "../../helper/products";
import ProductName from "../ProductName";

const TZ = "Australia/Sydney";

function formatDate(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);

  const formatter = new Intl.DateTimeFormat("en-AU", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return formatter.format(date);
}

// Optional: shows AEDT/AEST correctly instead of hardcoding "AEST"
function formatTzAbbrev(isoString) {
  if (!isoString) return "";
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: TZ,
    timeZoneName: "short",
  }).formatToParts(new Date(isoString));

  const tz = parts.find((p) => p.type === "timeZoneName")?.value;
  return tz ?? "";
}

function normalizeOrderHistory(userDetails) {
  if (!userDetails || typeof userDetails !== "object") return [];

  const orders = [];

  for (const [, value] of Object.entries(userDetails)) {
    const transaction = value.transaction;
    const rawItems = value.items;
    const items = rawItems.map((i) => {
      return new OrderItem(i.product_name, i.id, transaction.transaction_id, i.quantity, getDisplayPrice(i.sold_price), i.condition, i.variant, i.set_id, i.product_type);});

    orders.push({
      id: transaction.id,
      transaction,
      items,
    });
  }

  // newest first (by created_at, fallback to id)
  orders.sort((a, b) => {
    const ad = a.transaction?.created_at ? new Date(a.transaction.created_at).getTime() : 0;
    const bd = b.transaction?.created_at ? new Date(b.transaction.created_at).getTime() : 0;
    if (bd !== ad) return bd - ad;
    return (b.id ?? 0) - (a.id ?? 0);
  });

  return orders;
}

const OrderHistoryComponent = ({products}) => {
  const [orders, setOrders] = useState([]); // array, not Map
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let isMounted = true;

    (async () => {
      setIsLoading(true);
      setErrorMsg("");

      try {
        const userDetails = await getOrderHistory();
        const normalized = normalizeOrderHistory(userDetails);

        if (!isMounted) return;
        setOrders(normalized);
      } catch (e) {
        console.error(e);
        if (!isMounted) return;
        setErrorMsg("Failed to load order history.");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedOrder = useMemo(() => {
    if (selectedOrderId == null) return null;
    return orders.find((o) => String(o.id) === String(selectedOrderId)) ?? null;
  }, [orders, selectedOrderId]);

  if (isLoading) {
    return (
      <div className="order-container hide-scrollbar">
        <div style={{ padding: 16 }}>Loading orders…</div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="order-container hide-scrollbar">
        <div style={{ padding: 16 }}>{errorMsg}</div>
      </div>
    );
  }

  // LIST VIEW
  if (!selectedOrder) {
    if (!orders.length) {
      return (
        <div className="order-container hide-scrollbar">
          <div style={{ padding: 16 }}>No orders yet.</div>
        </div>
      );
    }

    return (
      <div className="order-container hide-scrollbar">
        {orders.map((o) => {
          const t = o.transaction;
          const previewItems = o.items.slice(0, 5);
          const remainingCount = Math.max(0, o.items.length - previewItems.length);

          return (
            <div
              key={o.id}
              className="order-preview"
              onClick={() => setSelectedOrderId(o.id)}
            >
              <h2>
                #{t.id} | {formatDate(t.created_at)} {formatTzAbbrev(t.created_at)} |{" "}
                {t.shipped ? "Shipped!" : "Not Shipped"}
                {t.shipping_paid ? " | Paid shipping" : ""}
              </h2>

              <div
                className="order-thumbnails"
                style={{ display: "flex", gap: "10px", marginTop: "10px", overflow:"hidden" }}
              >
                {previewItems.map((item) => (
                  <div key={item.id ?? `${o.id}-${item.name}`}>
                    <OrderItemImageComponent item={item} />
                  </div>
                ))}

                {remainingCount > 0 && (
                  <div
                    style={{
                      width: "50px",
                      height: "50px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(0,0,0,0.1)",
                      borderRadius: "6px",
                      fontWeight: "bold",
                    }}
                  >
                    +{remainingCount}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div className="padding-bottom"></div>
      </div>
    );
  }

  // DETAIL VIEW
  const t = selectedOrder.transaction;

  return (
    <div className="order-view-container hide-scrollbar">
      <div className="order-overview">
        <button onClick={() => setSelectedOrderId(null)} type="button">
          <h1>{"<-"} Order #{t.id}</h1>
        </button>

        {t.shipped ? (
          <a
            href={`https://auspost.com.au/mypost/track/details/${t.track_number}`}
            target="_blank"
            rel="noreferrer"
          >
            Tracking: {t.track_number}
          </a>
        ) : (
          <div>Not Yet Shipped</div>
        )}

        <div>Shipping paid [{t.shipping_paid ? "✔" : ""}]</div>
        <div>Payment status: {t.status}</div>
      </div>

      <div className="order-header spread">
        <h2>Image</h2>
        <h2>Item</h2>
        <h2>Condition</h2>
        <h2>Variant</h2>
        <h2>Price</h2>
        <h2>Quantity</h2>
      </div>

      {selectedOrder.items.map((item) => (
        <div className="order-item spread" key={item.id ?? `${selectedOrder.id}-${item.name}`}>
          <OrderBigItemImageComponent item={item} />
          <h2><ProductName name={item.name} type={item.type} useFullName stacked /></h2>
          <h2>{item.condition}</h2>
          <h2>{item.variant}</h2>
          <h2>${Number(item.sold_price || 0).toFixed(2)}</h2>
          <h2>{item.quantity}</h2>
        </div>
      ))}
      <div className="padding-bottom"></div>
    </div>
  );
};

export default OrderHistoryComponent;
